/**
 * StateMachine.ts - Game state management for DEFCON TERMINAL
 *
 * Manages high-level game states and transitions with guards.
 * States: BOOT, MENU, WAR_DIALER, CONNECTING, PLAYING, ENDING, LEADERBOARD
 */

import { EventEmitter, type EventMap } from './EventEmitter';

/**
 * Game states enum
 */
export enum GameStateType {
  BOOT = 'BOOT',
  MENU = 'MENU',
  WAR_DIALER = 'WAR_DIALER',
  CONNECTING = 'CONNECTING',
  PLAYING = 'PLAYING',
  ENDING = 'ENDING',
  LEADERBOARD = 'LEADERBOARD',
}

/**
 * State transition definition
 */
export interface StateTransition {
  from: GameStateType | GameStateType[];
  to: GameStateType;
  guard?: () => boolean;
  action?: () => void | Promise<void>;
}

/**
 * State machine configuration
 */
export interface StateMachineConfig {
  initial: GameStateType;
  transitions: Record<string, StateTransition>;
  onStateChange?: (from: GameStateType, to: GameStateType) => void;
}

/**
 * Events emitted by the StateMachine
 */
export interface StateMachineEvents extends EventMap {
  'state:enter': { state: GameStateType; from: GameStateType | null };
  'state:exit': { state: GameStateType; to: GameStateType };
  'state:transition': { from: GameStateType; to: GameStateType; transitionName: string };
  'state:blocked': { from: GameStateType; to: GameStateType; reason: string };
  'state:error': { error: Error; transition: string };
}

/**
 * Storage keys for state persistence
 */
const STORAGE_KEY = 'defcon_terminal_state';

/**
 * Default state machine configuration for DEFCON TERMINAL
 */
export function createDefaultConfig(): StateMachineConfig {
  return {
    initial: GameStateType.BOOT,
    transitions: {
      boot_to_menu: {
        from: GameStateType.BOOT,
        to: GameStateType.MENU,
      },
      menu_to_wardialer: {
        from: GameStateType.MENU,
        to: GameStateType.WAR_DIALER,
      },
      wardialer_to_connecting: {
        from: GameStateType.WAR_DIALER,
        to: GameStateType.CONNECTING,
      },
      connecting_to_playing: {
        from: GameStateType.CONNECTING,
        to: GameStateType.PLAYING,
      },
      playing_to_ending: {
        from: GameStateType.PLAYING,
        to: GameStateType.ENDING,
      },
      ending_to_leaderboard: {
        from: GameStateType.ENDING,
        to: GameStateType.LEADERBOARD,
      },
      leaderboard_to_menu: {
        from: GameStateType.LEADERBOARD,
        to: GameStateType.MENU,
      },
      // Direct transitions
      menu_to_leaderboard: {
        from: GameStateType.MENU,
        to: GameStateType.LEADERBOARD,
      },
      // Resume game transitions
      menu_to_playing: {
        from: GameStateType.MENU,
        to: GameStateType.PLAYING,
      },
      // Exit transitions
      playing_to_menu: {
        from: GameStateType.PLAYING,
        to: GameStateType.MENU,
      },
      wardialer_to_menu: {
        from: GameStateType.WAR_DIALER,
        to: GameStateType.MENU,
      },
      connecting_to_menu: {
        from: GameStateType.CONNECTING,
        to: GameStateType.MENU,
      },
      ending_to_menu: {
        from: GameStateType.ENDING,
        to: GameStateType.MENU,
      },
    },
  };
}

/**
 * StateMachine class - Manages game state transitions
 */
export class StateMachine extends EventEmitter<StateMachineEvents> {
  private currentState: GameStateType;
  private config: StateMachineConfig;
  private stateHistory: GameStateType[] = [];
  private isTransitioning: boolean = false;
  private stateData: Map<GameStateType, unknown> = new Map();
  private persistToStorage: boolean;
  private serverSyncCallback?: (state: GameStateType) => Promise<void>;

  constructor(
    config?: StateMachineConfig,
    options?: {
      persistToStorage?: boolean;
      serverSyncCallback?: (state: GameStateType) => Promise<void>;
    }
  ) {
    super();
    this.config = config ?? createDefaultConfig();
    this.currentState = this.config.initial;
    this.persistToStorage = options?.persistToStorage ?? true;
    this.serverSyncCallback = options?.serverSyncCallback;
  }

  /**
   * Get the current state
   */
  getState(): GameStateType {
    return this.currentState;
  }

  /**
   * Get state history
   */
  getHistory(): GameStateType[] {
    return [...this.stateHistory];
  }

  /**
   * Get data associated with a state
   */
  getStateData<T>(state?: GameStateType): T | undefined {
    return this.stateData.get(state ?? this.currentState) as T | undefined;
  }

  /**
   * Set data for a state
   */
  setStateData<T>(data: T, state?: GameStateType): void {
    this.stateData.set(state ?? this.currentState, data);
  }

  /**
   * Clear data for a state
   */
  clearStateData(state?: GameStateType): void {
    this.stateData.delete(state ?? this.currentState);
  }

  /**
   * Check if a transition is valid from current state
   */
  canTransitionTo(targetState: GameStateType): boolean {
    return this.findTransition(this.currentState, targetState) !== null;
  }

  /**
   * Find a valid transition between states
   */
  private findTransition(
    from: GameStateType,
    to: GameStateType
  ): { name: string; transition: StateTransition } | null {
    for (const [name, transition] of Object.entries(this.config.transitions)) {
      const fromStates = Array.isArray(transition.from)
        ? transition.from
        : [transition.from];

      if (fromStates.includes(from) && transition.to === to) {
        return { name, transition };
      }
    }
    return null;
  }

  /**
   * Transition to a new state
   *
   * @param targetState - State to transition to
   * @param data - Optional data to pass to the new state
   * @returns True if transition was successful
   */
  async transitionTo(
    targetState: GameStateType,
    data?: unknown
  ): Promise<boolean> {
    // Prevent concurrent transitions
    if (this.isTransitioning) {
      console.warn('Transition already in progress');
      return false;
    }

    // Same state check
    if (targetState === this.currentState) {
      console.warn(`Already in state: ${targetState}`);
      return true;
    }

    // Find valid transition
    const found = this.findTransition(this.currentState, targetState);
    if (!found) {
      this.emit('state:blocked', {
        from: this.currentState,
        to: targetState,
        reason: `No valid transition from ${this.currentState} to ${targetState}`,
      });
      return false;
    }

    const { name, transition } = found;

    // Check guard condition
    if (transition.guard && !transition.guard()) {
      this.emit('state:blocked', {
        from: this.currentState,
        to: targetState,
        reason: `Guard condition failed for transition: ${name}`,
      });
      return false;
    }

    try {
      this.isTransitioning = true;

      // Emit exit event
      this.emit('state:exit', {
        state: this.currentState,
        to: targetState,
      });

      // Execute transition action if defined
      if (transition.action) {
        await transition.action();
      }

      // Update state
      const previousState = this.currentState;
      this.stateHistory.push(previousState);
      this.currentState = targetState;

      // Store state data if provided
      if (data !== undefined) {
        this.setStateData(data, targetState);
      }

      // Emit enter event
      this.emit('state:enter', {
        state: targetState,
        from: previousState,
      });

      // Emit transition event
      this.emit('state:transition', {
        from: previousState,
        to: targetState,
        transitionName: name,
      });

      // Call config callback
      if (this.config.onStateChange) {
        this.config.onStateChange(previousState, targetState);
      }

      // Persist to localStorage
      if (this.persistToStorage) {
        this.saveToStorage();
      }

      // Sync to server
      if (this.serverSyncCallback) {
        try {
          await this.serverSyncCallback(targetState);
        } catch (error) {
          console.error('Failed to sync state to server:', error);
        }
      }

      return true;
    } catch (error) {
      this.emit('state:error', {
        error: error instanceof Error ? error : new Error(String(error)),
        transition: name,
      });
      return false;
    } finally {
      this.isTransitioning = false;
    }
  }

  /**
   * Execute a named transition
   *
   * @param transitionName - Name of the transition to execute
   * @param data - Optional data to pass to the new state
   */
  async executeTransition(
    transitionName: string,
    data?: unknown
  ): Promise<boolean> {
    const transition = this.config.transitions[transitionName];
    if (!transition) {
      console.warn(`Unknown transition: ${transitionName}`);
      return false;
    }

    return this.transitionTo(transition.to, data);
  }

  /**
   * Get available transitions from current state
   */
  getAvailableTransitions(): string[] {
    const available: string[] = [];

    for (const [name, transition] of Object.entries(this.config.transitions)) {
      const fromStates = Array.isArray(transition.from)
        ? transition.from
        : [transition.from];

      if (fromStates.includes(this.currentState)) {
        // Check guard if present
        if (!transition.guard || transition.guard()) {
          available.push(name);
        }
      }
    }

    return available;
  }

  /**
   * Check if currently in a specific state
   */
  isInState(state: GameStateType): boolean {
    return this.currentState === state;
  }

  /**
   * Check if in any of the given states
   */
  isInAnyState(states: GameStateType[]): boolean {
    return states.includes(this.currentState);
  }

  /**
   * Add a guard to an existing transition
   */
  addGuard(transitionName: string, guard: () => boolean): void {
    const transition = this.config.transitions[transitionName];
    if (transition) {
      const originalGuard = transition.guard;
      transition.guard = () => {
        if (originalGuard && !originalGuard()) {
          return false;
        }
        return guard();
      };
    }
  }

  /**
   * Add a new transition
   */
  addTransition(name: string, transition: StateTransition): void {
    this.config.transitions[name] = transition;
  }

  /**
   * Remove a transition
   */
  removeTransition(name: string): void {
    delete this.config.transitions[name];
  }

  /**
   * Save state to localStorage
   */
  private saveToStorage(): void {
    try {
      const data = {
        currentState: this.currentState,
        stateHistory: this.stateHistory.slice(-10), // Keep last 10 states
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save state to localStorage:', error);
    }
  }

  /**
   * Load state from localStorage
   */
  loadFromStorage(): boolean {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return false;
      }

      const data = JSON.parse(stored);

      // Validate stored state is a valid GameStateType
      if (!Object.values(GameStateType).includes(data.currentState)) {
        console.warn('Invalid stored state:', data.currentState);
        return false;
      }

      // Check if data is not too old (24 hours)
      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > maxAge) {
        console.warn('Stored state is too old, ignoring');
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }

      // Restore state
      this.currentState = data.currentState;
      this.stateHistory = data.stateHistory || [];

      return true;
    } catch (error) {
      console.error('Failed to load state from localStorage:', error);
      return false;
    }
  }

  /**
   * Clear stored state
   */
  clearStorage(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear state from localStorage:', error);
    }
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this.stateHistory = [];
    this.stateData.clear();
    this.currentState = this.config.initial;
    this.clearStorage();

    this.emit('state:enter', {
      state: this.currentState,
      from: null,
    });
  }

  /**
   * Export state for serialization
   */
  toJSON(): {
    currentState: GameStateType;
    stateHistory: GameStateType[];
  } {
    return {
      currentState: this.currentState,
      stateHistory: [...this.stateHistory],
    };
  }

  /**
   * Import state from serialized data
   */
  fromJSON(data: {
    currentState: GameStateType;
    stateHistory?: GameStateType[];
  }): void {
    if (Object.values(GameStateType).includes(data.currentState)) {
      this.currentState = data.currentState;
      this.stateHistory = data.stateHistory || [];
    }
  }
}

/**
 * Create a pre-configured state machine for DEFCON TERMINAL
 */
export function createGameStateMachine(options?: {
  persistToStorage?: boolean;
  serverSyncCallback?: (state: GameStateType) => Promise<void>;
}): StateMachine {
  return new StateMachine(createDefaultConfig(), options);
}
